import { useEffect, useCallback } from 'react';

declare global {
  interface Window {
    Cal?: any;
  }
}

interface CalButtonProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

// Global flag to ensure script loads only once
let calScriptLoaded = false;
let calInitialized = false;

export const CalButton = ({ children, className = '', style }: CalButtonProps) => {
  useEffect(() => {
    // Load Cal.com script only once globally
    if (calScriptLoaded) return;
    
    const existingScript = document.querySelector('script[src*="cal.com/embed"]');
    if (existingScript) {
      calScriptLoaded = true;
      return;
    }

    calScriptLoaded = true;
    const script = document.createElement('script');
    script.src = 'https://app.cal.com/embed/embed.js';
    script.async = true;
    
    script.onload = () => {
      console.log('✅ Cal.com script loaded successfully');
      
      // Wait for Cal to be available and initialize
      const checkCal = setInterval(() => {
        if (window.Cal && !calInitialized) {
          calInitialized = true;
          clearInterval(checkCal);
          
          try {
            window.Cal('init', { origin: 'https://app.cal.com' });
            console.log('✅ Cal.com initialized');
          } catch (error) {
            console.error('❌ Cal.com initialization error:', error);
          }
        }
      }, 100);
      
      // Stop checking after 5 seconds
      setTimeout(() => clearInterval(checkCal), 5000);
    };
    
    script.onerror = () => {
      console.error('❌ Failed to load Cal.com script');
      calScriptLoaded = false;
    };
    
    document.head.appendChild(script);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    
    // Try to open Cal.com modal
    if (window.Cal) {
      try {
        window.Cal('modal', {
          calLink: 'team-reflow-jvyyod/reflowai-discovery-call',
          config: {
            layout: 'month_view',
            theme: 'light'
          }
        });
        console.log('📅 Cal.com modal opened');
      } catch (error) {
        console.error('❌ Error opening Cal modal:', error);
        // Fallback to new tab
        window.open('https://cal.com/team-reflow-jvyyod/reflowai-discovery-call', '_blank');
      }
    } else {
      console.warn('⚠️ Cal not ready, opening in new tab');
      // Fallback to new tab
      window.open('https://cal.com/team-reflow-jvyyod/reflowai-discovery-call', '_blank');
    }
  }, []);

  return (
    <button
      onClick={handleClick}
      className={className}
      style={style}
      type="button"
    >
      {children}
    </button>
  );
};
